import { LabelProvider, external_provider_of, label_api_application_of } from "./provider";
import { computed, reactive } from "@vue/composition-api";
import { LabelTreshHold, MipLabel, MipOption2State } from "./model";
import * as api from "./api";
import { InformationType } from "@/common/models";
import { SharedState } from "@/common/state";
import { UseLoading } from "@/common/composition/loading";
import { common_state } from "./common.state";

const state = reactive({
  combinations: [],
} as MipOption2State);

const already_mapped_cognni_labels = computed(() =>
  state.combinations.reduce((acc, l) => {
    acc[l.labelId] = true;
    return acc;
  }, {} as Record<string, true>)
);

const init_overrides_state = async (
  mip_labels: MipLabel[]
): Promise<Record<string, string[]>> => {
  const res = await api.getAllLabelOverrides();
  if (!res.success) {
    return {};
  }
  const mip_id_name_obj = mip_labels.reduce(
    // @ts-ignore
    (acc, { labelId, labelName }) => {
      // @ts-ignore
      acc[labelId] = labelName;
      return acc;
    },
    {}
  );
  const overrides_obj = res.value.reduce(
    // @ts-ignore
    (acc: any, { labelId, externalLabelId }) => {
      acc[labelId] = [
        ...(acc[labelId] || []),
        // @ts-ignore
        mip_id_name_obj[externalLabelId],
      ];
      return acc;
    },
    {} as Record<string, string[]>
  );
  return overrides_obj;
};

const load_combinations = async () => {
  const res = await api.get_label_mapping_combinations({
    externalLabelProvider: external_provider_of(common_state.state.provider),
  });
  if (res.success) {
    // resolve override names against the provider's own labels: Google
    // external label ids are absent from the Microsoft tree, so passing the
    // Microsoft list here left every Google override rendering as undefined
    const overrides = await init_overrides_state(
      common_state.state.provider === "google"
        ? common_state.state.google_labels
        : common_state.state.mipLabelsTree.flat(1)
    );
    state.combinations = res.value
      .filter(
        ({ labelId, mipLabelId }) =>
          common_state.state.indexed_mip_labels[mipLabelId] &&
          SharedState.getInfoType(labelId) &&
          // @ts-ignore
          !SharedState.getInfoType(labelId).fake
      )
      .map(({ labelId, mipLabelId }) => {
        const label = SharedState.getInfoType(labelId);
        const parent = SharedState.getInfoType(label.parentId as string);
        const label_from_config = common_state.state.labels_config[labelId];
        const confidenceThreshold =
          label_from_config && label_from_config.confidenceThreshold
            ? label_from_config.confidenceThreshold
            : 0;
        const mip_label = common_state.state.indexed_mip_labels[mipLabelId];
        return {
          labelId,
          externalLabelId: mipLabelId,
          overrideLabelIds: overrides[labelId] || [],
          confidenceThreshold: Math.floor(
            (confidenceThreshold || 0) *
              (LabelTreshHold.max - LabelTreshHold.min) +
              LabelTreshHold.min
          ),
          runtime: {
            external_label_name: mip_label.labelName,
            label_name: label.labelName,
            parent_code: parent.code,
            parent_id: parent.id,
            mip_parent_name: mip_label.parentName,
          },
        };
      });
  }
};

/** a mapping's external label, as label-api's beta mapping takes it */
const external_label_of = (mip_label_id: string) => {
  const label = common_state.state.indexed_mip_labels[mip_label_id];
  return {
    labelId: mip_label_id,
    labelFullName: (label && label.labelFullName) || "",
    labelName: (label && label.labelName) || "",
  };
};

/**
 * Link and automate in one write on label-api's beta endpoint; a provider
 * without one links, then automates, through integrations-manager.
 */
const write_new_mapping = async (congnni_label: InformationType, mip_label: MipLabel) => {
  const application = label_api_application_of(common_state.state.provider);
  if (application) {
    return api.put_label_mapping_beta({
      labelId: congnni_label.id,
      application,
      external: {
        labelId: mip_label.labelId,
        labelFullName: mip_label.labelFullName,
        labelName: mip_label.labelName,
      },
      automate: true,
      confidenceThreshold: 0,
    });
  }
  const res = await api.create_label_mapping_combinations({
    labelId: congnni_label.id,
    mipLabelId: mip_label.labelId,
    mipLabelName: mip_label.labelName,
    mipLabelFullName: mip_label.labelFullName,
    externalLabelProvider: external_provider_of(common_state.state.provider),
  });
  if (res.success) {
    await api.updateCognniLabel({
      labelId: congnni_label.id,
      automate: true,
      confidenceThreshold: 0,
    });
  }
  return res;
};

const add_combination = async (
  congnni_label: InformationType,
  mip_label: MipLabel
) => {
  const res = await write_new_mapping(congnni_label, mip_label);
  if (res.success) {
    const parent_label = SharedState.getInfoType(
      congnni_label.parentId as string
    );
    state.combinations = [
      ...state.combinations,
      {
        confidenceThreshold: 67,
        externalLabelId: mip_label.labelId,
        labelId: congnni_label.id,
        overrideLabelIds: [],
        runtime: {
          label_name: congnni_label.labelName,
          external_label_name: mip_label.labelName,
          parent_code: parent_label.code,
          parent_id: parent_label.id,
          // @ts-ignore
          mip_parent_name: mip_label.parentName,
        },
      },
    ];
  }
};

const change_threshold_for_ui = (labelId: string, newTrashHold: number) => {
  state.combinations = state.combinations.map((comb) => {
    if (comb.labelId === labelId) {
      return {
        ...comb,
        confidenceThreshold: newTrashHold,
      };
    }
    return comb;
  });
};

const change_threshold = async (labelId: string, newTrashHold: number) => {
  const normalized_value = LabelTreshHold.normalize(newTrashHold);
  const application = label_api_application_of(common_state.state.provider);
  const combination = state.combinations.find((c) => c.labelId === labelId);
  // the beta mapping is written whole: the same external label, still
  // automated, at the new threshold
  const res =
    application && combination
      ? await api.put_label_mapping_beta({
          labelId,
          application,
          external: external_label_of(combination.externalLabelId),
          automate: true,
          confidenceThreshold: normalized_value,
        })
      : await api.updateCognniLabel({
          labelId: labelId,
          confidenceThreshold: normalized_value,
        });
  if (res.success) {
    state.combinations = state.combinations.map((l) => {
      if (l.labelId === labelId) {
        l.confidenceThreshold = newTrashHold;
      }
      return l;
    });
  }
};

const delete_combination = async (cognni_label_id: string) => {
  const application = label_api_application_of(common_state.state.provider);
  if (application) {
    // one call unlinks and turns automation and overrides off
    const res = await api.delete_label_mapping_beta({ labelId: cognni_label_id, application });
    if (res.success) {
      common_state.mutate_override_all_status(cognni_label_id, false);
      state.combinations = state.combinations.filter((l) => l.labelId !== cognni_label_id);
    }
    return;
  }
  const overrides_res = await api.getLabelOverrides(cognni_label_id);
  const res = await api.delete_label_mapping_combinations({
    labelId: cognni_label_id,
    externalLabelProvider: external_provider_of(common_state.state.provider),
  });
  if (res.success && overrides_res.success) {
    await api.updateCognniLabel({
      labelId: cognni_label_id,
      automate: false,
      override: false,
    });
    common_state.mutate_override_all_status(cognni_label_id, false);
    if (overrides_res.value.length > 0) {
      await api.updateOverrides(
        overrides_res.value.map((l) => {
          l.override = false;
          l.externalLabelProvider = external_provider_of(
            common_state.state.provider
          );
          return l;
        })
      );
    }
    state.combinations = state.combinations.filter(
      (l) => l.labelId !== cognni_label_id
    );
  }
};

const update_combination_overrides = (labelId: string, overrides: string[]) => {
  state.combinations = state.combinations.map((cmb) => {
    if (cmb.labelId === labelId) {
      return {
        ...cmb,
        overrideLabelIds: overrides.map(
          (id) => common_state.state.indexed_mip_labels[id].labelName
        ),
      };
    }
    return cmb;
  });
};

const [is_loading, set_is_loading] = UseLoading(true).as_arr();

/**
 * Which provider's mappings are currently in `state.combinations`.
 *
 * This was `once()`, which latched the fetch for the whole page load: whichever
 * labeling screen you opened first won, and switching to the other kept showing
 * the first one's rows until a manual refresh. Unlike the label lists, mappings
 * cannot simply be preloaded together — the table renders one provider's rows —
 * so the latch is keyed by provider instead of being global.
 */
let loaded_for: LabelProvider | null = null;

const init = async () => {
  const provider = common_state.state.provider;
  if (loaded_for === provider) return;
  loaded_for = provider;
  // Drop the previous provider's rows before fetching, not after. The table
  // treats "loading with rows already present" as a refresh and keeps showing
  // them, so leaving them here rendered Microsoft mappings under the Google
  // heading for the length of the request.
  state.combinations = [];
  set_is_loading(true);
  await load_combinations();
  set_is_loading(false);
};

export const automate_state = {
  state,
  init,
  is_loading,
  update_combination_overrides,
  already_mapped_cognni_labels,
  add_combination,
  delete_combination,
  change_threshold_for_ui,
  change_threshold,
};
